import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import StatusBadge from '../../../components/base/StatusBadge';
import {
  deleteDemoTenant,
  fetchDemoTenantsList,
  type DemoTenantListItem,
} from '../../../api/demoTenants.api';
import CreateDemoTenantModal, { type CreatedDemoResult } from './CreateDemoTenantModal';
import PromoteDemoTenantModal from './PromoteDemoTenantModal';
import CredentialsPopup from '../../order-plans/base/CredentialsPopup';
import { formatTenantDate } from '../../../lib/tenant.utils';
import LoginAsTenantButton from '../../../components/feature/LoginAsTenantButton';
import { usePlatformPermissions } from '../../../hooks/usePlatformPermissions';

const PER_PAGE = 15;

type DemoListMode = 'active' | 'expired';

export default function DemoTenantsTable({ mode = 'active' }: { mode?: DemoListMode }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can } = usePlatformPermissions();
  const isExpiredMode = mode === 'expired';
  const i18nRoot = isExpiredMode ? 'expired_demo_tenants' : 'demo_tenants';

  const [rows, setRows] = useState<DemoTenantListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'suspended'>('all');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [promoteTenant, setPromoteTenant] = useState<DemoTenantListItem | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [credentials, setCredentials] = useState<{
    isOpen: boolean;
    email: string;
    password: string;
    tenantName?: string;
    loginUrl?: string;
  }>({ isOpen: false, email: '', password: '' });

  const canAdd = !isExpiredMode && can('add_demo_tenant');
  const canDelete = can('delete_demo_tenant');
  const canPromote = can('promote_demo_tenant');

  const loadList = useCallback(async () => {
    setLoading(true);
    setError('');
    const r = await fetchDemoTenantsList(page, PER_PAGE, {
      lifecycle: isExpiredMode ? 'expired' : 'active',
      status: !isExpiredMode && statusFilter !== 'all' ? statusFilter : undefined,
      search: search.trim() || undefined,
    });
    setLoading(false);
    if (r.ok === false) {
      if (r.unauthorized) {
        navigate('/admin/login', { replace: true });
        return;
      }
      setError(r.message);
      setRows([]);
      return;
    }
    setRows(r.list.data);
    setLastPage(Math.max(1, r.list.last_page));
    setTotal(r.list.total);
  }, [navigate, page, search, statusFilter, isExpiredMode]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  const handleDelete = async (row: DemoTenantListItem) => {
    const confirmed = window.confirm(
      t('demo_tenants.delete.confirm', { name: row.name }),
    );
    if (!confirmed) return;

    setError('');
    setSuccessMessage('');
    setDeletingId(row.id);
    const result = await deleteDemoTenant(row.id);
    setDeletingId(null);
    if (result.ok === false) {
      if (result.unauthorized) {
        navigate('/admin/login', { replace: true });
        return;
      }
      setError(result.message);
      return;
    }
    setSuccessMessage(t('demo_tenants.delete.success'));
    void loadList();
  };

  const filters: { value: typeof statusFilter; labelKey: string }[] = [
    { value: 'all', labelKey: 'demo_tenants.filter.all' },
    { value: 'active', labelKey: 'demo_tenants.filter.active' },
    { value: 'suspended', labelKey: 'demo_tenants.filter.suspended' },
  ];

  const activeCount = rows.filter((r) => r.status === 'active').length;
  const expiredCount = rows.filter((r) => r.status === 'expired').length;
  const suspendedCount = rows.filter((r) => r.status === 'suspended').length;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 ring-1 ring-gray-100">
          <p className="text-xs text-gray-500 font-medium">{t(`${i18nRoot}.summary.total`)}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{loading ? '—' : total}</p>
        </div>
        {isExpiredMode ? (
          <>
            <div className="bg-white rounded-xl p-4 ring-1 ring-gray-100">
              <p className="text-xs text-gray-500 font-medium">{t(`${i18nRoot}.summary.expired`)}</p>
              <p className="text-2xl font-bold text-rose-600 mt-1">{loading ? '—' : expiredCount}</p>
            </div>
            <div className="bg-white rounded-xl p-4 ring-1 ring-gray-100">
              <p className="text-xs text-gray-500 font-medium">{t(`${i18nRoot}.summary.suspended`)}</p>
              <p className="text-2xl font-bold text-amber-600 mt-1">{loading ? '—' : suspendedCount}</p>
            </div>
          </>
        ) : (
          <>
            <div className="bg-white rounded-xl p-4 ring-1 ring-gray-100">
              <p className="text-xs text-gray-500 font-medium">{t(`${i18nRoot}.summary.active`)}</p>
              <p className="text-2xl font-bold text-emerald-600 mt-1">{loading ? '—' : activeCount}</p>
            </div>
            <div className="bg-white rounded-xl p-4 ring-1 ring-gray-100">
              <p className="text-xs text-gray-500 font-medium">{t(`${i18nRoot}.summary.suspended`)}</p>
              <p className="text-2xl font-bold text-amber-600 mt-1">{loading ? '—' : suspendedCount}</p>
            </div>
          </>
        )}
      </div>

      {error ? (
        <div className="rounded-xl bg-rose-50 text-rose-800 text-sm px-4 py-3 ring-1 ring-rose-100">{error}</div>
      ) : null}

      {successMessage ? (
        <div className="rounded-xl bg-emerald-50 text-emerald-800 text-sm px-4 py-3 ring-1 ring-emerald-100">
          {successMessage}
        </div>
      ) : null}

      <div className="bg-white rounded-xl ring-1 ring-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            {!isExpiredMode
              ? filters.map((f) => (
                  <button
                    key={f.value}
                    type="button"
                    onClick={() => {
                      setStatusFilter(f.value);
                      setPage(1);
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer ${
                      statusFilter === f.value
                        ? 'bg-teal-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {t(f.labelKey)}
                  </button>
                ))
              : (
                <span className="px-3 py-1.5 rounded-full text-xs font-medium bg-rose-50 text-rose-700">
                  {t('demo_tenants.filter.expired')}
                </span>
              )}
          </div>
          <div className="flex items-center gap-3">
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder={t('demo_tenants.search_placeholder')}
              className="w-48 px-3 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30"
            />
            {canAdd ? (
              <button
                type="button"
                onClick={() => setCreateOpen(true)}
                className="flex items-center gap-2 px-4 py-1.5 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 cursor-pointer"
              >
                <i className="ri-add-line" />
                {t('demo_tenants.add')}
              </button>
            ) : null}
          </div>
        </div>

        <p className="px-5 py-2 text-xs text-gray-500 bg-gray-50/80 border-b border-gray-50">
          {t(`${i18nRoot}.banner`)}
        </p>

        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3 text-gray-500">
              <i className="ri-loader-4-line text-3xl text-teal-600 animate-spin" />
              <p className="text-sm">{t(`${i18nRoot}.loading`)}</p>
            </div>
          ) : (
            <table className="w-full min-w-[900px]">
              <thead className="bg-gray-50/80">
                <tr>
                  {[
                    'name',
                    'email',
                    'created_by',
                    'days',
                    'remaining',
                    'ends_at',
                    'status',
                    'created',
                    'actions',
                  ].map((col) => (
                    <th
                      key={col}
                      className="px-4 py-3 text-start text-xs font-semibold text-gray-500 uppercase tracking-wide"
                    >
                      {t(`demo_tenants.col.${col}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-16 text-center text-gray-400 text-sm">
                      {t(`${i18nRoot}.empty`)}
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50/60">
                      <td className="px-4 py-3">
                        <p className="text-sm font-semibold text-gray-900">{row.name}</p>
                        <p className="text-xs font-mono text-gray-400">{row.slug}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 break-all">
                        {row.admin_email || row.email || '—'}
                      </td>
                      <td className="px-4 py-3">
                        {row.created_by?.name || row.created_by?.email ? (
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {row.created_by.name || '—'}
                            </p>
                            <p className="text-xs text-gray-400 break-all">
                              {row.created_by.email || ''}
                            </p>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm font-mono text-gray-700">
                        {row.demo_days ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-orange-700">
                        {row.days_remaining != null
                          ? t('demo_tenants.days_left', { count: row.days_remaining })
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                        {formatTenantDate(row.subscription_ends_at || row.trial_ends_at)}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={row.status || (isExpiredMode ? 'expired' : 'active')} />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                        {formatTenantDate(row.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          {canPromote ? (
                            <button
                              type="button"
                              onClick={() => {
                                setSuccessMessage('');
                                setPromoteTenant(row);
                              }}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-teal-50 text-teal-700 hover:bg-teal-100 cursor-pointer"
                              title={t('demo_tenants.promote.action')}
                            >
                              <i className="ri-vip-crown-line" />
                              {t('demo_tenants.promote.action')}
                            </button>
                          ) : null}
                          <button
                            type="button"
                            onClick={() => navigate(`/demo-tenants/${row.id}/performance`)}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-gray-700 hover:text-teal-700 cursor-pointer"
                          >
                            <i className="ri-eye-line" />
                            {t('demo_tenants.view_performance')}
                          </button>
                          <LoginAsTenantButton
                            tenantId={row.id}
                            variant="link"
                            label={t('demo_tenants.open_login')}
                          />
                          {canDelete ? (
                            <button
                              type="button"
                              disabled={deletingId === row.id}
                              onClick={() => void handleDelete(row)}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-rose-50 text-rose-700 hover:bg-rose-100 disabled:opacity-50 cursor-pointer"
                              title={t('demo_tenants.delete.action')}
                            >
                              <i
                                className={
                                  deletingId === row.id
                                    ? 'ri-loader-4-line animate-spin'
                                    : 'ri-delete-bin-line'
                                }
                              />
                              {t('demo_tenants.delete.action')}
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-50 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            {t('table.showing')} {rows.length === 0 ? 0 : (page - 1) * PER_PAGE + 1}–
            {(page - 1) * PER_PAGE + rows.length} {t('table.of')} {total}
          </p>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="w-8 h-8 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30 cursor-pointer"
            >
              <i className="ri-arrow-right-s-line" />
            </button>
            <span className="text-sm text-gray-600 px-2">
              {page} / {lastPage}
            </span>
            <button
              type="button"
              disabled={page >= lastPage || loading}
              onClick={() => setPage((p) => p + 1)}
              className="w-8 h-8 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30 cursor-pointer"
            >
              <i className="ri-arrow-left-s-line" />
            </button>
          </div>
        </div>
      </div>

      {canAdd ? (
        <CreateDemoTenantModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={(result: CreatedDemoResult) => {
            void loadList();
            setCredentials({
              isOpen: true,
              email: result.email,
              password: result.password,
              tenantName: `${result.tenantName} (${t('demo_tenants.days_label', { days: result.days })})`,
              loginUrl: result.loginUrl,
            });
          }}
        />
      ) : null}

      {canPromote ? (
        <PromoteDemoTenantModal
          open={!!promoteTenant}
          tenant={promoteTenant}
          onClose={() => setPromoteTenant(null)}
          onPromoted={() => {
            setSuccessMessage(t('demo_tenants.promote.success'));
            void loadList();
          }}
        />
      ) : null}

      <CredentialsPopup
        isOpen={credentials.isOpen}
        onClose={() => setCredentials((p) => ({ ...p, isOpen: false }))}
        email={credentials.email}
        password={credentials.password}
        tenantName={credentials.tenantName}
        loginUrl={credentials.loginUrl}
      />
    </div>
  );
}
